import { useFormikContext } from 'formik';
import { get } from 'lodash-es';
import React, { useEffect, useRef } from 'react';

import * as errors from 'utils/errors';

import Checkbox, { CheckboxProps } from '../../Checkbox';
import { ComponentProps as DropdownProps } from '../../Dropdown';
import SingleAddress, { ComponentProps as SingleAddressProps } from '../Single';
import * as helpers from '../helpers';
import * as Interfaces from '../interfaces';
import Info, { ComponentProps as InfoProps } from './Info';
import * as Styled from './styled';

export type ComponentProps = InfoProps & {
  /**
   * local address component props
   */
  localProps: SingleAddressProps;
  /**
   * Optional label string to show next to checking if the given address is an overseas address (defaults to 'This address is overseas')
   */
  isAddressOverseasLabel?: string;
  /**
   * full text area props (basic mode) for customisation when needed
   */
  fullAddressTextAreaProps?: React.TextareaHTMLAttributes<HTMLTextAreaElement>;
  /**
   * country dropdown props for customisation when needed
   */
  countryDropDownProps?: Partial<DropdownProps>;
  /**
   * overseas checkbox props
   */
  overseasCheckBoxProps?: Partial<CheckboxProps>;
  /**
   * The address type. If it's overseas, it will only show overseas address component.
   * If it's local, it will only show a single address component.
   * If it's both, it will have a checkbox to switch between local and overseas addres.
   *
   * Default value is both
   */
  addressType?: 'both' | 'overseas' | 'local';
};

export const Overseas: React.FC<ComponentProps> = ({
  fieldName,
  localProps,
  mode,
  loadCountriesHandler,
  fullAddressTextAreaProps,
  countryDropDownProps,
  overseasCheckBoxProps,
  isAddressOverseasLabel = 'This address is overseas',
  addressType = 'both'
}) => {
  // access formik context data
  const { values, setFieldValue, setFieldTouched } = useFormikContext<any>();
  const addressValues = get(values, fieldName) as Interfaces.OverseasAddressData;
  if (!addressValues) {
    throw new errors.SVError(
      `invalid fieldName "${fieldName}" was provided to the <Overseas /> address component`
    );
  }
  const { isOverseasAddress } = addressValues;
  const prevIsOverseasAddress = useRef<boolean>(isOverseasAddress);

  /**
   * Reset the overseas address information when the "isOverseasAddress" checkbox flag is off.
   * Note: This needs to be in a seperate effect, as the home address changes shouldn't have an effect on postal address changes while its been edited (both can be edited seperately)
   */
  useEffect(() => {
    // Reset the overseas information only if user triggered a change to the tickbox of "isOverseasAddress" flag
    if (isOverseasAddress !== prevIsOverseasAddress.current) {
      prevIsOverseasAddress.current = isOverseasAddress;
      setFieldValue(`${fieldName}.local`, helpers.initialSingleAddressValues, false);
      setFieldValue(`${fieldName}.overseas`, helpers.initialOverseasAddressValues, false);
      if (!isOverseasAddress) {
        setFieldTouched(
          `${fieldName}.local`,
          helpers.initialSingleAddressTouched(false) as any,
          false
        );
      } else {
        setFieldTouched(
          `${fieldName}.overseas`,
          helpers.initialOverseasAddressTouched(false) as any,
          false
        );
      }
    }
  }, [fieldName, isOverseasAddress, setFieldValue, setFieldTouched]);

  useEffect(() => {
    // Set the isOverseasAddress depending on the address type
    if (addressType === 'overseas' && !isOverseasAddress) {
      setFieldValue(`${fieldName}.isOverseasAddress`, true);
    } else if (addressType === 'local' && isOverseasAddress) {
      setFieldValue(`${fieldName}.isOverseasAddress`, false);
    }
  }, [addressType, fieldName, isOverseasAddress, setFieldValue]);

  return (
    <Styled.Wrapper>
      {addressType === 'both' && (
        <Styled.AddressContainer>
          <Checkbox
            name={`${fieldName}.isOverseasAddress`}
            label={isAddressOverseasLabel}
            {...overseasCheckBoxProps}
          />
        </Styled.AddressContainer>
      )}
      {!isOverseasAddress
        ? ['both', 'local'].includes(addressType) && <SingleAddress {...localProps} />
        : ['both', 'overseas'].includes(addressType) && (
            <Info
              fieldName={fieldName}
              mode={mode}
              loadCountriesHandler={loadCountriesHandler}
              fullAddressTextAreaProps={fullAddressTextAreaProps}
              countryDropDownProps={countryDropDownProps}
            />
          )}
    </Styled.Wrapper>
  );
};

export default Overseas;
